// Patient cockpit — the "pull up patient → message → take notes side-by-side" workspace. 3 toggleable
// columns: Information | Messages | Encounter notes. REAL data: brief (demographics + Class-B recaps),
// encrypted messages, the encrypted huddle prep note, and — alongside it — the structured SOAP note
// (the FB-authorized synthetic demo clinical store, THU-346; encrypted + RLS + audited). The full
// clinical chart record still lives in Healthie (the hand-off in the Information column).
import "../styles-cockpit.css";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useApi } from "../api";
import { useAsync } from "../lib/useAsync";
import { age, formatDateTime } from "../lib/format";
import { Btn } from "@championcare/ui";
import { Loading, ErrorState } from "../components/ui";
import { EMR_LOGIN_URL } from "../components/EmrHandoff";
import { ClinicalFacts } from "../components/ClinicalFacts";
import { PrescribePanel } from "../components/PrescribePanel";
import { SoapPanel } from "../components/SoapPanel";
import { IntakeRecall } from "../components/IntakeRecall";

type Section = "info" | "messages" | "notes";

const Ic = {
  info: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>,
  msg: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>,
  notes: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="9" y1="13" x2="15" y2="13" /><line x1="9" y1="17" x2="15" y2="17" /></svg>,
  send: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>,
  pen: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>,
};


export function PatientDetail() {
  const { id = "" } = useParams();
  const api = useApi();
  const brief = useAsync(() => api.patientBrief(id), [id]);
  const [show, setShow] = useState<Record<Section, boolean>>({ info: true, messages: true, notes: true });
  // Prescribe modal open state lives here so the trigger can sit in the hero (top, left of Open EMR)
  // while the prescriptions module itself stays down in the Information column.
  const [rxOpen, setRxOpen] = useState(false);

  if (brief.loading) return <Loading />;
  if (brief.error) return <ErrorState message={brief.error} />;
  if (!brief.data) return <ErrorState message="Patient not found." />;

  const p = brief.data.patient;
  const initials = `${p.first_name.charAt(0)}${p.last_name.charAt(0)}`.toUpperCase();
  const lastSeen = brief.data.visit_summaries[0]?.last_visit_at;

  const toggle = (s: Section) => setShow((v) => ({ ...v, [s]: !v[s] }));
  const wsClass = `pp-workspace ${!show.info ? "hide-info" : ""} ${!show.messages ? "hide-msg" : ""} ${!show.notes ? "hide-notes" : ""}`;

  return (
    <>
      {/* TOP ROW — back link (left) */}
      <div className="pp-topbar">
        <div className="pp-back">
          <Link to="/patients">&larr; All patients</Link>
        </div>
      </div>

      {/* HERO HEADER — patient card; Open EMR pinned to the top-right corner */}
      <div className="pp-hero">
        <div className="pp-hero-avatar">{initials}</div>
        <div className="pp-hero-main">
          <h1 className="pp-hero-name">{p.first_name} {p.last_name}</h1>
          <div className="pp-hero-stats">
            <span className="pp-hero-stat">{age(p.dob)}y{p.dob ? ` · DOB ${p.dob}` : ""}</span>
            {p.email && <span className="pp-hero-stat">{p.email}</span>}
            <span className="pp-hero-stat"><strong>{p.insurance_carrier ?? "—"}</strong></span>
            {lastSeen && <span className="pp-hero-stat">Last seen <strong>{formatDateTime(lastSeen)}</strong></span>}
          </div>
          <div className="pp-hero-pills">
            <span className="pp-pill-label">Clinical record</span>
            <span className="tag">In the chart</span>
          </div>
        </div>
        <div className="pp-hero-actions">
          <Btn as="button" variant="ghost" className="pp-hero-prescribe" onClick={() => setRxOpen(true)}>
            {Ic.pen}Prescribe
          </Btn>
          <Btn as="button" variant="primary" onClick={() => window.open(EMR_LOGIN_URL, "_blank", "noopener,noreferrer")}>
            Open EMR
          </Btn>
        </div>
      </div>

      {/* COLUMN TOGGLES — left-aligned, between the patient card and the columns */}
      <div className="pp-workspace-controls">
        <span className="pp-controls-label">Show:</span>
        <div className="pp-controls-chips">
          <button className={`pp-ws-toggle ${show.info ? "active" : ""}`} onClick={() => toggle("info")}>{Ic.info}<span>Information</span></button>
          <button className={`pp-ws-toggle ${show.messages ? "active" : ""}`} onClick={() => toggle("messages")}>{Ic.msg}<span>Messages</span></button>
          <button className={`pp-ws-toggle ${show.notes ? "active" : ""}`} onClick={() => toggle("notes")}>{Ic.notes}<span>Encounter notes</span></button>
        </div>
      </div>

      {/* WORKSPACE */}
      <div className={wsClass}>
        {/* INFO */}
        <aside className="pp-info-panel">
          <header className="pp-panel-head"><div className="pp-panel-title">{Ic.info}<span>Patient <em>information</em></span></div></header>
          <div className="pp-info-body">
            <div className="pp-info-block">
              <h4>Demographics</h4>
              <div className="pp-info-rows">
                <div className="row"><span className="lab">Age</span><span className="val">{age(p.dob)}y</span></div>
                <div className="row"><span className="lab">DOB</span><span className="val">{p.dob ?? "—"}</span></div>
                <div className="row"><span className="lab">Phone</span><span className="val">{p.phone ?? "—"}</span></div>
                <div className="row"><span className="lab">Email</span><span className="val">{p.email ?? "—"}</span></div>
                <div className="row"><span className="lab">Membership</span><span className="val" style={{ textTransform: "capitalize" }}>{p.tier}</span></div>
              </div>
            </div>
            <div className="pp-info-block">
              <h4>Insurance</h4>
              <div className="pp-info-text">{p.insurance_carrier ?? "No carrier on file"}{p.insurance_member_ref ? ` · ${p.insurance_member_ref}` : ""}</div>
            </div>
            <div className="pp-info-block">
              <h4>Recent visit recaps <span className="pp-count">({brief.data.visit_summaries.length})</span></h4>
              {brief.data.visit_summaries.length ? (
                <div className="pp-info-timeline">
                  {brief.data.visit_summaries.slice(0, 6).map((s) => (
                    <div className="pp-info-timeline-item" key={s.id}>
                      <div className="date">{formatDateTime(s.last_visit_at)}</div>
                      <div className="title">{s.summary_text}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="pp-info-text muted">No prior visits.</span>
              )}
            </div>
            {/* Pre-visit intake (booking step 01) — recalled from the encrypted demo store, RLS-scoped. */}
            <IntakeRecall patientId={id} />
            {/* Editable demo clinical facts (THU-346 Slice 2) — persisted, encrypted, RLS-scoped. */}
            <ClinicalFacts patientId={id} />
            {/* Prescribe (THU-346 Slice 3) — MOCK e-prescribe persisted to the demo store; no pharmacy.
                The module/list stays here; its trigger lives in the hero (top, left of Open EMR). */}
            <PrescribePanel patientId={id} open={rxOpen} onClose={() => setRxOpen(false)} />
          </div>
        </aside>

        {/* MESSAGES */}
        <MessagesColumn patientId={id} firstName={p.first_name} />

        {/* ENCOUNTER NOTES — structured S/O/A/P (the demo clinical store) */}
        <aside className="pp-notes-panel">
          <SoapPanel patientId={id} />
        </aside>
      </div>
    </>
  );
}

function MessagesColumn({ patientId, firstName }: { patientId: string; firstName: string }) {
  const api = useApi();
  const messages = useAsync(() => api.messages(patientId), [patientId]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  const list = messages.data?.messages ?? [];
  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight });
  }, [list.length]);

  async function send() {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await api.sendMessage(patientId, text);
      setDraft("");
      messages.reload();
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="pp-messages-panel">
      <header className="pp-panel-head">
        <div className="pp-panel-title">{Ic.msg}<span><em>Messages</em></span></div>
        <span className="pp-panel-meta">{list.length} message{list.length === 1 ? "" : "s"}</span>
      </header>
      <div className="pp-messages-body" ref={bodyRef}>
        {list.length ? (
          list.map((m) => (
            <div className={`msg-wrap from-${m.sender_role}`} key={m.id}>
              <div className={`msg from-${m.sender_role}`}>{m.body}</div>
              <div className="msg-time">{formatDateTime(m.created_at)}</div>
            </div>
          ))
        ) : (
          <div className="pp-messages-empty">No messages yet with {firstName}. Send the first one below.</div>
        )}
      </div>
      <div className="pp-messages-compose">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); } }}
          placeholder={`Send a secure message to ${firstName}…`}
          rows={1}
          aria-label="Message"
        />
        <button className="btn-send" onClick={() => void send()} disabled={!draft.trim() || sending} title="Send">{Ic.send}</button>
      </div>
    </section>
  );
}

